import { WeatherData } from "@/utils/DryingProgressGraphDataExtractor";
import { functions } from "@/main";
import axios from "axios";
import {
  getCountryCodeByCity as _getCountryCodeByCity,
  makeGeoEncodeRequest as _makeGeoEncodeRequest,
} from "@/utils/GeocodingService";

export const DEFAULT_COUNTRY_CODE = "DE";

/**
 * Re-exported from `GeocodingService` for backward compatibility.
 * New consumers should import directly from `@/utils/GeocodingService`.
 */
export const getCountryCodeByCity = _getCountryCodeByCity;

/**
 * Re-exported from `GeocodingService` for backward compatibility.
 * New consumers should import directly from `@/utils/GeocodingService`.
 */
export const makeGeoEncodeRequest = _makeGeoEncodeRequest;

/**
 * Groups Open-Meteo hourly response data into per-date WeatherData arrays.
 * Deduplicates by timestamp to handle overlapping archive/forecast responses.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function groupHourlyDataByDate(hourly: any, dates: string[]): WeatherData[][] {
  const dataByDate = new Map<string, WeatherData[]>();
  const seenTimestamps = new Set<string>();
  for (const date of dates) {
    dataByDate.set(date, []);
  }

  for (let i = 0; i < hourly.time.length; i++) {
    const timestamp = hourly.time[i];
    if (seenTimestamps.has(timestamp)) continue;
    seenTimestamps.add(timestamp);

    const date = timestamp.substring(0, 10); // "YYYY-MM-DD"

    if (dataByDate.has(date)) {
      const entries = dataByDate.get(date);
      if (entries) {
        entries.push({
          date,
          timestamp,
          temperature: hourly.temperature_2m[i],
          relativeHumidity: hourly.relative_humidity_2m[i],
          pressureMsl: hourly.pressure_msl[i],
          precipitation: hourly.precipitation[i],
        });
      }
    }
  }

  return dates.map(date => dataByDate.get(date) ?? []);
}

/**
 * Groups Bright Sky weather response data into per-date WeatherData arrays.
 * Bright Sky returns an array of weather records with individual timestamps.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function groupBrightSkyDataByDate(weatherRecords: any[], dates: string[]): WeatherData[][] {
  const dataByDate = new Map<string, WeatherData[]>();
  for (const date of dates) {
    dataByDate.set(date, []);
  }

  for (const record of weatherRecords) {
    const timestamp: string = record.timestamp;
    const date = timestamp.substring(0, 10);

    if (dataByDate.has(date)) {
      const entries = dataByDate.get(date);
      if (entries) {
        entries.push({
          date,
          timestamp,
          temperature: record.temperature ?? null,
          relativeHumidity: record.relative_humidity ?? null,
          pressureMsl: record.pressure_msl ?? null,
          precipitation: record.precipitation ?? null,
        });
      }
    }
  }

  return dates.map(date => dataByDate.get(date) ?? []);
}

/** Returns true if at least one date has weather entries. */
function hasWeatherData(data: WeatherData[][]): boolean {
  return data.some(entries => entries.length > 0);
}

/**
 * Checks whether Bright Sky data has sufficient quality for display.
 * Bright Sky may return records for remote locations where most fields (temperature,
 * humidity, pressure) are null — only precipitation from radar is available.
 * For locations outside Germany, recent MOSMIX forecasts may have temperature data
 * while historical observations don't, so we check both halves independently.
 */
function hasSufficientBrightSkyData(data: WeatherData[][]): boolean {
  const allEntries = data.flat();
  if (allEntries.length === 0) return false;

  // Density check: expect ~24 hourly entries per date. Reject if we get
  // fewer than 12 per date on average (sparse MOSMIX-only responses for
  // non-DE locations).
  const expectedMinEntries = data.length * 12;

  const withTemperature = allEntries.filter(e => e.temperature != null).length;
  const overallRatio = withTemperature / allEntries.length;

  if (allEntries.length < expectedMinEntries) return false;
  if (overallRatio < 0.5) return false;

  return true;
}

/** Request timeout for Bright Sky API calls (ms). */
const BRIGHT_SKY_TIMEOUT_MS = 15000;
/** Request timeout for Open-Meteo direct calls (ms). */
const OPEN_METEO_TIMEOUT_MS = 5000;
/** Request timeout for Cloud Function proxy calls (ms). */
const CF_TIMEOUT_MS = 8000;

/** Number of days into the past that the Open-Meteo forecast API reliably provides data. */
const FORECAST_PAST_DAYS = 5;

/** Open-Meteo hourly parameters used by both archive and forecast endpoints. */
const OPEN_METEO_HOURLY_PARAMS = "temperature_2m,relative_humidity_2m,pressure_msl,precipitation";

/**
 * Returns the ISO date string (YYYY-MM-DD) for the boundary between
 * the archive and forecast API ranges. Dates before this should use
 * the archive API; dates on or after can use the forecast API.
 */
function getArchiveForecastBoundary(): string {
  const boundary = new Date();
  boundary.setDate(boundary.getDate() - FORECAST_PAST_DAYS);
  return boundary.toISOString().split("T")[0];
}

/**
 * Merges two WeatherData[][] arrays by date index.
 * Deduplicates by timestamp, preferring archive entries over forecast entries
 * for the same hour. This avoids doubled density near the archive/forecast boundary.
 */
function mergeWeatherDataByDate(
  archive: WeatherData[][],
  forecast: WeatherData[][],
  dates: string[]
): WeatherData[][] {
  return dates.map((_, i) => {
    const archiveEntries = archive[i] ?? [];
    const forecastEntries = forecast[i] ?? [];

    const seen = new Set(archiveEntries.map(e => e.timestamp));
    const uniqueForecast = forecastEntries.filter(e => !seen.has(e.timestamp));
    return [...archiveEntries, ...uniqueForecast];
  });
}

/**
 * Fetches historical weather data from the Open-Meteo Archive API.
 * Covers dates older than ~5 days where the forecast API has no data.
 */
async function fetchFromOpenMeteoArchive(
  lat: number,
  lon: number,
  startDate: string,
  endDate: string,
  dates: string[]
): Promise<WeatherData[][]> {
  const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}`
    + `&hourly=${OPEN_METEO_HOURLY_PARAMS}`
    + `&start_date=${startDate}&end_date=${endDate}&timezone=auto`;

  const response = await axios.get(url, { timeout: OPEN_METEO_TIMEOUT_MS });
  const hourly = response.data?.hourly;

  if (!hourly?.time) {
    return dates.map(() => []);
  }

  return groupHourlyDataByDate(hourly, dates);
}

/**
 * Fetches weather data from the Open-Meteo Forecast API.
 * Only reliable for the last ~5 days and up to 16 days into the future.
 */
async function fetchFromOpenMeteoForecast(
  lat: number,
  lon: number,
  startDate: string,
  endDate: string,
  dates: string[]
): Promise<WeatherData[][]> {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
    + `&hourly=${OPEN_METEO_HOURLY_PARAMS}`
    + `&start_date=${startDate}&end_date=${endDate}&timezone=auto`;

  const response = await axios.get(url, { timeout: OPEN_METEO_TIMEOUT_MS });
  const hourly = response.data?.hourly;

  if (!hourly?.time) {
    return dates.map(() => []);
  }

  return groupHourlyDataByDate(hourly, dates);
}

/**
 * Fetches weather data from the Bright Sky API (DWD open data).
 * Free, CORS-enabled, no API key required. Best coverage for Germany and
 * surrounding regions; forecasts available globally via DWD MOSMIX.
 */
async function fetchWeatherViaBrightSky(
  lat: number,
  lon: number,
  dates: string[]
): Promise<WeatherData[][]> {
  const sortedDates = [...dates].sort();
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  const url = `https://api.brightsky.dev/weather?lat=${lat}&lon=${lon}`
    + `&date=${startDate}&last_date=${endDate}T23:59`;

  const response = await axios.get(url, { timeout: BRIGHT_SKY_TIMEOUT_MS });
  const weatherRecords = response.data?.weather;

  if (!Array.isArray(weatherRecords) || weatherRecords.length === 0) {
    throw new Error("No weather data from Bright Sky");
  }

  const result = groupBrightSkyDataByDate(weatherRecords, dates);

  if (!hasWeatherData(result)) {
    throw new Error("Bright Sky returned no matching data for requested dates");
  }

  if (!hasSufficientBrightSkyData(result)) {
    throw new Error("Bright Sky data lacks temperature/humidity for this location");
  }

  return result;
}

/**
 * Fetches weather data via a Firebase Cloud Function proxy.
 * The Cloud Function calls the Open-Meteo API server-side, avoiding CORS restrictions.
 * Passes the archive/forecast boundary so the CF can split historical vs. recent dates.
 */
async function fetchWeatherViaCloudFunction(
  lat: number,
  lon: number,
  dates: string[]
): Promise<WeatherData[][]> {
  const sortedDates = [...dates].sort();
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];
  const archiveBoundary = getArchiveForecastBoundary();

  const callable = functions.httpsCallable("fetchWeatherData", { timeout: CF_TIMEOUT_MS });
  const result = await callable({ lat, lon, startDate, endDate, archiveBoundary });

  const hourly = result.data?.hourly;

  if (!hourly?.time) {
    throw new Error("No weather data from Cloud Function");
  }

  return groupHourlyDataByDate(hourly, dates);
}

/**
 * Fetches weather data directly from the Open-Meteo API.
 * Automatically splits between the Archive API (for historical dates) and
 * the Forecast API (for recent/future dates). May fail with CORS in the browser.
 */
async function fetchWeatherDirectly(
  lat: number,
  lon: number,
  dates: string[]
): Promise<WeatherData[][]> {
  const sortedDates = [...dates].sort();
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  const boundary = getArchiveForecastBoundary();

  // All dates are recent — use forecast API only
  if (startDate >= boundary) {
    const result = await fetchFromOpenMeteoForecast(lat, lon, startDate, endDate, dates);
    if (!hasWeatherData(result)) {
      throw new Error("No weather data from Open-Meteo forecast");
    }
    return result;
  }

  // All dates are historical — use archive API only
  if (endDate < boundary) {
    const result = await fetchFromOpenMeteoArchive(lat, lon, startDate, endDate, dates);
    if (!hasWeatherData(result)) {
      throw new Error("No weather data from Open-Meteo archive");
    }
    return result;
  }

  // Date range spans both — fetch in parallel and merge
  const archiveDates = dates.filter(d => d < boundary);
  const forecastDates = dates.filter(d => d >= boundary);
  const archiveEnd = archiveDates[archiveDates.length - 1];
  const forecastStart = forecastDates[0];

  const [archiveData, forecastData] = await Promise.all([
    fetchFromOpenMeteoArchive(lat, lon, startDate, archiveEnd, dates),
    fetchFromOpenMeteoForecast(lat, lon, forecastStart, endDate, dates),
  ]);

  const merged = mergeWeatherDataByDate(archiveData, forecastData, dates);
  if (!hasWeatherData(merged)) {
    throw new Error("No weather data from Open-Meteo archive+forecast");
  }
  return merged;
}

/**
 * Fetches weather information for given coordinates and dates.
 * Fallback chain: Bright Sky (DWD) → Cloud Function proxy → Open-Meteo direct.
 * Returns `null` if all sources fail (caller should handle as weather unavailable).
 *
 * @param lat The latitude of the location for which weather data is requested.
 * @param lon The longitude of the location for which weather data is requested.
 * @param dates Array of dates in ISO format (YYYY-MM-DD) for which weather data is needed.
 * @returns Weather data per date, or `null` if all sources failed.
 */
export async function fetchWeatherInformation(
  lat: number,
  lon: number,
  dates: string[]
): Promise<WeatherData[][] | null> {
  if (dates.length === 0) {
    return [];
  }

  // 1. Try Bright Sky first (DWD station data, best quality for Germany).
  //    Awaited with a generous timeout to avoid premature fallback to
  //    Open-Meteo whose interpolated grid data may differ from DWD stations.
  try {
    const brightSkyResult = await fetchWeatherViaBrightSky(lat, lon, dates);
    if (brightSkyResult) {
      return brightSkyResult;
    }
  } catch {
    // Bright Sky failed — continue to fallbacks
  }

  // 2. Cloud Function proxy (Open-Meteo server-side, avoids CORS)
  try {
    const cloudFunctionResult = await fetchWeatherViaCloudFunction(lat, lon, dates);
    if (cloudFunctionResult) {
      return cloudFunctionResult;
    }
  } catch {
    // Cloud Function failed — continue to direct fallback
  }

  // 3. Open-Meteo direct (last resort, may fail with CORS in the browser)
  try {
    const directResult = await fetchWeatherDirectly(lat, lon, dates);
    return directResult;
  } catch {
    return null;
  }
}
